import axios, { type AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import { milliseconds } from 'date-fns';

import { paths } from '~/constants/paths';
import { useRootLoader } from '~/hooks/useUser';
import type { Session } from '~/types/auth';
import { getTokenExpiration, isTokenExpiringSoon } from '~/utils/token';

export type FlaskApiConfig = {
  baseURL: string;
  user: Session | null;
};

class FlaskApiClient {
  private token?: string;
  private baseURL?: string;
  private refreshPromise: Promise<string> | null = null;
  private instance: AxiosInstance | null = null;

  configure(config: FlaskApiConfig) {
    const nextToken = config.user?.idToken;
    const baseURLChanged = this.baseURL !== config.baseURL;

    const currentExp = this.token ? getTokenExpiration(this.token) : null;
    const nextExp = nextToken ? getTokenExpiration(nextToken) : null;

    const noCurrentToken = !this.token;
    const nextTokenIsNewer = nextExp !== null && (currentExp === null || nextExp > currentExp);

    const shouldUpdateToken = noCurrentToken || nextTokenIsNewer;

    if (baseURLChanged || shouldUpdateToken) {
      this.refreshPromise = null;
    }

    this.baseURL = config.baseURL;

    if (shouldUpdateToken) {
      this.token = nextToken;
    }
  }

  private async refreshToken(): Promise<string> {
    if (this.refreshPromise) return this.refreshPromise;

    this.refreshPromise = axios
      .get<{ idToken: string }>('/refresh-token', {
        headers: { Accept: 'application/json' },
        timeout: milliseconds({ seconds: 8 }),
      })
      .then((res) => {
        const idToken = res.data.idToken;

        if (typeof idToken !== 'string' || !idToken) {
          throw new Error('Refresh token response did not include idToken');
        }

        this.token = idToken;
        return idToken;
      })
      .finally(() => {
        this.refreshPromise = null;
      });

    return this.refreshPromise;
  }

  private async ensureFreshToken(): Promise<string | undefined> {
    if (!this.baseURL) {
      throw new Error('Flask API not initialized - call useInitializeFlaskApi() first');
    }

    if (!this.token || isTokenExpiringSoon(this.token)) {
      try {
        await this.refreshToken();
      } catch {
        redirectToLogin();
        throw new Error('Session expired');
      }
    }

    return this.token;
  }

  getBaseURL(): string {
    if (!this.baseURL) {
      throw new Error('Flask API not initialized - call useInitializeFlaskApi() first');
    }

    return this.baseURL;
  }

  async fetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    const sourceRequest = new Request(input, init);
    const url = toFlaskApiFetchUrl(this.getBaseURL(), sourceRequest.url);
    const body = await bufferRequestBody(sourceRequest);

    const response = await globalThis.fetch(
      url,
      await this.buildAuthorizedFetchInit(sourceRequest, body)
    );

    if (response.status !== 401) {
      return response;
    }

    try {
      await this.refreshToken();
    } catch {
      redirectToLogin();
      throw new Error('Session expired');
    }

    return globalThis.fetch(url, await this.buildAuthorizedFetchInit(sourceRequest, body));
  }

  private async buildAuthorizedFetchInit(
    request: Request,
    body: ArrayBuffer | undefined
  ): Promise<RequestInit> {
    const token = await this.ensureFreshToken();
    const headers = new Headers(request.headers);

    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    } else {
      headers.delete('Authorization');
    }

    return {
      body: body?.slice(0),
      headers,
      method: request.method,
      signal: request.signal,
    };
  }

  async heartbeat(): Promise<void> {
    await this.refreshToken();
  }

  getInstance(): AxiosInstance {
    if (this.instance) return this.instance;

    this.instance = axios.create();

    this.instance.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
      const token = await this.ensureFreshToken();

      config.baseURL = this.getBaseURL();
      const headers = config.headers as Record<string, string | undefined>;
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      } else {
        delete headers.Authorization;
      }
      return config;
    });

    this.instance.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const config = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

        if (error.response?.status === 401 && config && !config._retry) {
          config._retry = true;
          try {
            await this.refreshToken();
            const headers = config.headers as Record<string, string | undefined>;
            if (this.token) {
              headers.Authorization = `Bearer ${this.token}`;
            } else {
              delete headers.Authorization;
            }
            return this.instance?.request(config);
          } catch {
            redirectToLogin();
            return;
          }
        }
        return Promise.reject(error);
      }
    );

    return this.instance;
  }
}

const flaskApiClient = new FlaskApiClient();

export function toFlaskApiFetchUrl(baseURL: string, url: string): string {
  const normalizedBaseURL = baseURL.endsWith('/') ? baseURL.slice(0, -1) : baseURL;

  if (url.startsWith(normalizedBaseURL)) {
    return url;
  }

  const requestUrl = new URL(url);
  const pathname = requestUrl.pathname.startsWith('/api/')
    ? requestUrl.pathname.slice('/api'.length)
    : requestUrl.pathname;

  return `${normalizedBaseURL}${pathname}${requestUrl.search}`;
}

async function bufferRequestBody(request: Request): Promise<ArrayBuffer | undefined> {
  if (request.method === 'GET' || request.method === 'HEAD' || !request.body) {
    return undefined;
  }

  return request.arrayBuffer();
}

export function useInitializeFlaskApi() {
  const { apiConfig } = useRootLoader();

  // Route components render during SSR. Keep this module importable server-side,
  // but only configure the process-global client singleton in the browser.
  if (typeof window === 'undefined') return;

  if (!apiConfig) {
    throw new Error('API config is required');
  }

  flaskApiClient.configure(apiConfig);
}

export function useFlaskApi() {
  return flaskApiClient.getInstance();
}

export function flaskApi() {
  return flaskApiClient.getInstance();
}

export function flaskApiFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  return flaskApiClient.fetch(input, init);
}

export function flaskApiHeartbeat(): Promise<void> {
  return flaskApiClient.heartbeat();
}

function redirectToLogin() {
  if (typeof window === 'undefined') return;
  window.location.href = paths.login(window.location.pathname);
}
